import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import * as _lodash from 'lodash';
import { FilterPanelConfig, FilterPanelEvents, FilterPanelActions } from '../../../widgets/filter-item/filter-panel/filter-panel.view-model';
import { FieldConfig, FieldConfigData, FieldEventType } from '../../../widgets/field-selector/field-selector.model';
import { StandardFilterItems, StandardFileFilterItems } from './email-digest-filter.model';
import { FilterService } from '../../../tamalelibs/services/filter.service';
import { FilterMetadataType } from '../../../tamalelibs/components/filter-config';
import { adhocTitleSelector } from '../../../redux/reducers/adhoc.reducer';
import { EmailDigestFilterService } from '../../../tamalelibs/services/email-digest-filter.service';
import { EntityService } from '../../../tamalelibs/services/entity.service';
import { AppState } from '../../../redux';
import { ALL_VALUE, CURRENT_USER } from '../../../tamalelibs/constants/business.constants';
import { EmailSection } from '../../../tamalelibs/models/emaildigest.model';

@Component({
    selector: 'tam-email-digest-filter',
    templateUrl: './email-digest-filter.component.html',
    styleUrls: ['./email-digest-filter.component.scss']
})
export class EmailDigestFilterComponent implements OnInit, OnChanges, OnDestroy {
    @Input() config: EmailSection;
    @Input() minuteOffset: number;

    filterConfig: FilterPanelConfig = new FilterPanelConfig();
    fieldConfig: FieldConfig = new FieldConfig();
    isNoteSection = true;

    private _dropdownSource: Array<any> = [];
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _emailDigestService: EmailDigestFilterService,
        private _entityService: EntityService,
        private _filterService: FilterService,
        private _store: Store<AppState>,
    ) { }

    ngOnInit() {
        this.filterConfig.isHiddenTrashIcon = false;
        this._store.pipe(
            select(adhocTitleSelector),
            take(1),
        ).subscribe(data => {
            if (data && data.size > 0) {
                data.forEach((item) => {
                    this._dropdownSource.push(item);
                });
                this.initFilter();
            }
        });
        this.fieldConfigSubject();
        this.filterConfigSubject();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.config = changes.config.currentValue;
        this.initFieldSelector();
        this.initFilter();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    getNewEntityInfo() {
        const entityIds = [];
        const needUpdate = (item) => {
            return item.metadataType === FilterMetadataType.Entity ||
                item.metadataType === FilterMetadataType.ED ||
                item.metadataType === FilterMetadataType.MED ||
                item.metadataType === FilterMetadataType.MET ||
                item.metadataType === FilterMetadataType.Participants ||
                item.metadataType === FilterMetadataType.Source ||
                item.metadataType === FilterMetadataType.Submitter ||
                item.metadataType === FilterMetadataType.Team ||
                item.metadataType === FilterMetadataType.Entities;
        };
        this.filterConfig.filtersConfigList.forEach(item => {
            if (needUpdate(item) && item.value) {
                item.value.forEach(val => {
                    if (val.id !== CURRENT_USER.id) {
                        entityIds.push(val.id);
                    }
                });
                const index = item.value.findIndex(data => data.id === CURRENT_USER.id);
                if (index !== -1) {
                    item.value[index] = CURRENT_USER;
                }
            }
        });
        this.filterConfig.action$.next({ type: FilterPanelActions.reloadFilter, payload: this.filterConfig.filtersConfigList });
        if (entityIds.length !== 0) {
            this._entityService.getEntityListByIdsQuick(entityIds).pipe(
                map(response => this._entityService.mapEntityBriefList(response)),
                map(entities => {
                    const entityMap = new Map();
                    entities.forEach(element => entityMap.set(element.id, element));
                    return entityMap;
                })
            ).subscribe((entityMap => {
                this.filterConfig.filtersConfigList.forEach(item => {
                    if (needUpdate(item) && item.value) {
                        item.value = item.value.map(element => {
                            if (entityMap.has(element.id)) {
                                return entityMap.get(element.id);
                            }
                            return element;
                        });
                        item.value = item.value.filter(value => !!value);
                    }
                });
                this.filterConfig.action$.next({ type: FilterPanelActions.reloadFilter, payload: this.filterConfig.filtersConfigList });
            }));
        }
    }

    initFilter() {
        this.filterConfig.filtersConfigList = [];
        this.config.filterSetting.forEach(item => {
            if (item.metadataType === FilterMetadataType.Dropdown || item.metadataType === FilterMetadataType.MSD ||
                item.metadataType === FilterMetadataType.CheckBox) {
                const dropdown = this._dropdownSource.filter(data => {
                    return item.fields.findIndex(field => field === data.attrDefId[0]) !== -1;
                });
                if (dropdown && dropdown[0]) {
                    item['source'] = dropdown[0].source.map(value => ({
                        name: value,
                        value: value
                    }));
                }
            }
            const filterConfig = this._emailDigestService.convertEmailFilterToFilter(item, this.config.type);
            if (filterConfig) {
                this.filterConfig.filtersConfigList.push(filterConfig);
            }
        });
        this.filterConfig.action$.next({ type: FilterPanelActions.reloadFilter, payload: this.filterConfig.filtersConfigList });
        this.getNewEntityInfo();
    }

    initFieldSelector() {
        this.fieldConfig.config = new FieldConfigData();
        this.fieldConfig.config.standardItem = StandardFilterItems;
        if (this.config.type === 'files') {
            this.isNoteSection = false;
            this.fieldConfig.config.standardItem = StandardFileFilterItems;
        } else {
            this.isNoteSection = true;
        }
        this.fieldConfig.config.showAdhoc = true;
        this.fieldConfig.config.selector = adhocTitleSelector;
        this.fieldConfig.config.showSearch = true;
    }

    filterConfigSubject() {
        this._destroySubscriptions.push(
            this.filterConfig.events$.pipe(
                filter((data) => data.type === FilterPanelEvents.change || data.type === FilterPanelEvents.delete)
            ).subscribe((data) => {
                this.filterConfig.filtersConfigList = _lodash.cloneDeep(data.payload);
                this._formatFilterData();
            })
        );
    }

    fieldConfigSubject() {
        this._destroySubscriptions.push(
            this.fieldConfig.events$.pipe(
                filter(data => data.type === FieldEventType.select)
            ).subscribe((data) => {
                let filtersConfig;
                if (data.payload.type === 'standard') {
                    filtersConfig = this._filterService.getNewFilterDefaultInstance(data.payload.text,
                        data.payload.value, false, false, true);
                } else {
                    filtersConfig = this._emailDigestService.getAdhocFilterDefaultInstance(data.payload);
                }
                this.filterConfig.filtersConfigList.push(filtersConfig);
                this.filterConfig.action$.next({ type: FilterPanelActions.addFilter, payload: [filtersConfig] });
                this._formatFilterData();
            })
        );
    }

    private _formatFilterData() {
        const filters = [];
        // tslint:disable-next-line: no-shadowed-variable
        this.filterConfig.filtersConfigList.forEach(filter => {
            if (filter.metadataType === FilterMetadataType.Subject) {
                const config = {
                    fields: filter.fields,
                    metadataType: FilterMetadataType.TextBox,
                    operator: filter.selectedOperator,
                    title: filter.title,
                    value: filter.value
                };
                if (this.config.type === 'files') {
                    filter.fields = ['file subject'];
                    config.fields = ['file subject'];
                }
                filters.push(config);
            } else if (filter.metadataType === FilterMetadataType.Entities) {
                filters.push({
                    fields: filter.fields,
                    metadataType: filter.metadataType,
                    operator: filter.selectedOperator,
                    title: filter.title,
                    value: filter.value.map(item => item.id),
                    relationship: filter.relationshipType
                });
            } else if (filter.metadataType === FilterMetadataType.Source || filter.metadataType === FilterMetadataType.Submitter || filter.metadataType === FilterMetadataType.Participants) {
                filters.push({
                    fields: filter.fields,
                    metadataType: filter.metadataType,
                    operator: filter.selectedOperator,
                    title: filter.title,
                    value: filter.value.map(item => item.id)
                });
            } else if (filter.metadataType === FilterMetadataType.Team) {
                filters.push({
                    fields: filter.fields,
                    metadataType: filter.metadataType,
                    operator: this._formatTeamOperator(filter.selectedOperator),
                    title: filter.title,
                    value: filter.value.map(item => item.id)
                });
            } else if (filter.metadataType === FilterMetadataType.TextBox ||
                filter.metadataType === FilterMetadataType.Date) {
                const config = {
                    id: filter.id,
                    fields: filter.fields,
                    metadataType: filter.metadataType,
                    operator: filter.selectedOperator,
                    title: filter.title,
                    value: filter.value
                };
                if (filter.dataType) {
                    config['dateType'] = filter.dataType;
                }
                filters.push(config);
            } else if (filter.metadataType === FilterMetadataType.Dropdown ||
                filter.metadataType === FilterMetadataType.MSD ||
                filter.metadataType === FilterMetadataType.CheckBox) {
                const config = {
                    id: filter['adhocId'],
                    fields: filter.fields,
                    metadataType: filter.metadataType,
                    operator: filter.selectedOperator,
                    title: filter.title,
                    value: filter.value
                };
                if (filter.dataType) {
                    config['dateType'] = filter.dataType;
                }
                filters.push(config);

            } else if (filter.metadataType === FilterMetadataType.Entity ||
                filter.metadataType === FilterMetadataType.ED ||
                filter.metadataType === FilterMetadataType.MED ||
                filter.metadataType === FilterMetadataType.MET) {
                const config = {
                    id: filter.id,
                    fields: filter.fields,
                    metadataType: filter.metadataType,
                    operator: filter.selectedOperator,
                    title: filter.title,
                    value: filter.value.map(item => item.id)
                };
                if (filter.dataType) {
                    config['dateType'] = filter.dataType;
                }
                filters.push(config);
            } else {
                filters.push({
                    fields: filter.fields,
                    metadataType: filter.metadataType,
                    operator: filter.selectedOperator,
                    title: filter.title,
                    value: filter.value
                });
            }
        });
        let advFitlerString;
        if (this.config.type === 'files') {
            advFitlerString = this._filterService.parseFilterSettingToAdvFilter(this.filterConfig.filtersConfigList, ALL_VALUE,
                this.minuteOffset, 'FILES');
        } else {
            advFitlerString = this._filterService.parseFilterSettingToAdvFilter(this.filterConfig.filtersConfigList, ALL_VALUE,
                this.minuteOffset, 'NOTES');
        }
        this.config.filterSetting = filters;
        this.config.advFilter = advFitlerString;
    }

    private _formatTeamOperator(operator) {
        const operators = {
            'submittedby': 'Submitted by',
            'notsubmittedby': 'Not Submitted by',
            'sourcedto': 'Sourced to',
            'notsourcedto': 'Not Sourced to'
        };
        return operators[operator];
    }
}
